import { pickT } from "@repo/shared";
import {
  type ChartData,
  type ForecastChartData,
  type MultiLineData,
  type StackedBarDatum,
  type StackedBarReferenceLine,
  TABLEAU20_COLORS,
  type TimeSeriesData,
  type ValueHistoryDatum,
} from "@/lib/utils/vega-chart-shared";
import { T } from "./vega-specs.i18n";

/**
 * Compute a y-axis domain with "nice" round gridlines and a fixed number of
 * extra gridlines of headroom past the data extent — so a chart never has its
 * top series jammed against the top edge, and a modest series isn't blown up by
 * a tight auto-scale. Shared by the forecast and line charts (see DESIGN.md →
 * Charts).
 *
 * `dataMin` defaults to 0 (the common case — most series are non-negative and
 * should stay anchored at zero). When the data dips below zero (e.g. an
 * expected-value forecast where withdrawals deplete the portfolio), pass the
 * real minimum so the domain rounds down to a nice negative floor instead of
 * clipping the series — otherwise the out-of-domain area geometry overflows the
 * plot and Vega's "pad" autosize balloons the SVG height.
 */
function niceYAxisWithHeadroom(
  dataMax: number,
  extraGridlines: number,
  dataMin = 0,
): { domainMin: number; domainMax: number; tickValues: number[] } {
  const span = dataMax - dataMin;
  const rawStep = span > 0 ? span / 5 : 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceUnit = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const tickStep = niceUnit * magnitude;
  // Non-negative data stays anchored at 0; negative data rounds down to a nice
  // floor with one tick of headroom below.
  const domainMin =
    dataMin < 0 ? Math.floor(dataMin / tickStep) * tickStep - extraGridlines * tickStep : 0;
  const domainMax = Math.ceil(dataMax / tickStep) * tickStep + extraGridlines * tickStep;
  const tickValues: number[] = [];
  for (let v = domainMin; v <= domainMax + tickStep / 2; v += tickStep) tickValues.push(v);
  return { domainMin, domainMax, tickValues };
}

/**
 * Creates a Vega-Lite pie chart specification
 *
 * This spec can be used both server-side (with vega.View)
 * and client-side (with react-vega's VegaLite component).
 *
 * Returns a plain object that can be safely passed to Client Components.
 */
export function createPieChartSpec(
  data: ChartData[],
  options: {
    width?: number;
    height?: number;
    innerRadius?: number;
    title?: string;
    isDark?: boolean;
    chartColors?: string[];
    lang?: string;
  } = {},
): any {
  const width = options.width || 500;
  const height = options.height || 400;
  const innerRadius = options.innerRadius || 0.5;
  const isDark = options.isDark || false;
  const colors = options.chartColors || TABLEAU20_COLORS;
  const labels = pickT(T, options.lang ?? "en_GB");

  // Transform data for Vega-Lite with custom colors
  const vegaData = data.map((d, index) => ({
    category: d.label,
    value: d.value,
    color: d.color || colors[index % colors.length],
  }));

  // Calculate radii (Vega uses absolute pixels)
  const size = Math.min(width, height);
  const outerRadius = size / 2 - 20; // Less padding since no labels
  const innerRadiusPixels = outerRadius * innerRadius;

  // Theme colors
  const backgroundColor = isDark ? "transparent" : "transparent";

  const _textColor = isDark ? "#e5e7eb" : "#374151";

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    ...(options.title && { title: options.title }),
    width,
    height,
    background: backgroundColor,
    data: { values: vegaData },
    mark: {
      type: "arc",
      innerRadius: innerRadiusPixels,
      outerRadius: outerRadius,
      tooltip: true,
    },
    encoding: {
      theta: {
        field: "value",
        type: "quantitative",
        stack: true,
      },
      color: {
        field: "color",
        type: "nominal",
        scale: null,
        legend: null,
      },
      tooltip: [
        { field: "category", type: "nominal", title: labels.category },
        { field: "value", type: "quantitative", title: labels.percentage, format: ".1f" },
      ],
    },
    view: {
      stroke: null,
    },
  };
}

// Default primary color
const DEFAULT_PRIMARY_COLOR = "#ee8438";

/**
 * Creates a Vega-Lite line chart specification for time series data
 *
 * This spec can be used both server-side and client-side.
 */
export function createLineChartSpec(
  data: TimeSeriesData[],
  options: {
    width?: number | "container";
    height?: number;
    title?: string;
    yAxisLabel?: string;
    isDark?: boolean;
    primaryColor?: string;
    lang?: string;
    /**
     * When set, the y-axis is zero-based with this many extra round gridlines of
     * headroom above the data max (e.g. 2 for base-100 indexed return charts) so
     * the series never touches the top edge and modest series aren't blown up by
     * a tight auto-scale. Leave unset to auto-scale.
     */
    extraYGridlines?: number;
    /** Rate behind the bank comparison series — shown in the legend label. */
    bankReturnPct?: number;
  } = {},
): any {
  // Use numeric width - 'container' doesn't work reliably in all contexts
  const width = typeof options.width === "number" ? options.width : 500;
  const height = options.height || 400;
  const isDark = options.isDark || false;
  const labels = pickT(T, options.lang ?? "en_GB");

  // Bank-savings comparison line — present only when the data carries a bank
  // series; the chart then switches to a layered two-line spec with a legend.
  const hasBank = data.some((d) => typeof d.bank === "number" && Number.isFinite(d.bank));
  const yValues = (d: TimeSeriesData) =>
    [d.value, d.bank].filter((v): v is number => typeof v === "number" && Number.isFinite(v));

  // Axis with N gridlines of headroom past the data extent. The domain follows
  // the data below zero too (e.g. a depleting expected-value series), so the
  // line/area never overflows the plot and inflates the SVG.
  const yAxis =
    options.extraYGridlines !== undefined
      ? niceYAxisWithHeadroom(
          data.length > 0 ? Math.max(...data.flatMap(yValues)) : 0,
          options.extraYGridlines,
          data.length > 0 ? Math.min(...data.flatMap(yValues), 0) : 0,
        )
      : undefined;

  // Theme colors
  const backgroundColor = isDark ? "transparent" : "transparent";
  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const lineColor = options.primaryColor || DEFAULT_PRIMARY_COLOR;

  if (hasBank) {
    const bankLineColor = isDark ? "#9ca3af" : "#6b7280";
    const planLabel = labels.savingPlan;
    const bankLabel =
      options.bankReturnPct != null
        ? `${labels.bankSavings} – ${Number(options.bankReturnPct)}%`
        : labels.bankSavings;
    const colorScale = { domain: [planLabel, bankLabel], range: [lineColor, bankLineColor] };
    const bottomLegend = {
      orient: "bottom",
      direction: "horizontal",
      title: null,
      labelColor: textColor,
    };
    const hoverTooltip = [
      { field: "date", type: "temporal", title: labels.date, format: "%Y-%m-%d" },
      { field: "value", type: "quantitative", title: planLabel, format: ",.0f" },
      { field: "bank", type: "quantitative", title: bankLabel, format: ",.0f" },
    ];

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      width,
      height,
      background: backgroundColor,
      data: { values: data },
      layer: [
        // Area fill under the plan line (same styling as the single-series spec)
        {
          mark: {
            type: "area",
            clip: true,
            color: lineColor,
            opacity: 0.15,
            interpolate: "monotone",
          },
          encoding: {
            x: { field: "date", type: "temporal" },
            y: { field: "value", type: "quantitative" },
            tooltip: hoverTooltip,
          },
        },
        // Plan line — named so it shares the color scale/legend with the bank line
        {
          mark: { type: "line", clip: true, strokeWidth: 2, interpolate: "monotone" },
          encoding: {
            x: { field: "date", type: "temporal" },
            y: { field: "value", type: "quantitative" },
            color: { datum: planLabel, type: "nominal", scale: colorScale, legend: bottomLegend },
            tooltip: hoverTooltip,
          },
        },
        // Bank-savings comparison line (dashed gray)
        {
          mark: {
            type: "line",
            clip: true,
            strokeWidth: 2,
            strokeDash: [4, 4],
            interpolate: "monotone",
          },
          encoding: {
            x: { field: "date", type: "temporal" },
            y: { field: "bank", type: "quantitative" },
            color: { datum: bankLabel, type: "nominal", scale: colorScale, legend: bottomLegend },
            tooltip: hoverTooltip,
          },
        },
      ],
      encoding: {
        x: {
          axis: {
            title: null,
            labelColor: textColor,
            tickColor: textColor,
            grid: false,
            domain: false,
            tickCount: 5,
            labelAngle: 0,
            format: "%Y",
          },
        },
        y: {
          ...(yAxis ? { scale: { domainMin: yAxis.domainMin, domainMax: yAxis.domainMax } } : {}),
          axis: {
            title: null,
            labelColor: textColor,
            tickColor: textColor,
            gridColor: gridColor,
            domain: false,
            orient: "left",
            ...(yAxis ? { values: yAxis.tickValues } : { tickCount: 5 }),
          },
        },
      },
      config: {
        view: { stroke: "transparent" },
      },
    };
  }

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height,
    background: backgroundColor,
    data: { values: data },
    mark: {
      type: "area",
      // Clip to the plot so any out-of-domain geometry can't overflow and
      // balloon the SVG height under Vega's "pad" autosize.
      clip: true,
      line: {
        color: lineColor,
        strokeWidth: 2,
      },
      color: lineColor,
      opacity: 0.15,
      interpolate: "monotone",
      tooltip: true,
    },
    encoding: {
      x: {
        field: "date",
        type: "temporal",
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          tickCount: 5,
          labelAngle: 0,
          format: "%Y",
        },
      },
      y: {
        field: "value",
        type: "quantitative",
        ...(yAxis ? { scale: { domainMin: yAxis.domainMin, domainMax: yAxis.domainMax } } : {}),
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor: gridColor,
          domain: false,
          orient: "left",
          ...(yAxis ? { values: yAxis.tickValues } : { tickCount: 5 }),
        },
      },
      tooltip: [
        { field: "date", type: "temporal", title: labels.date, format: "%Y-%m-%d" },
        { field: "value", type: "quantitative", title: labels.value, format: ".2f" },
      ],
    },
    config: {
      view: {
        stroke: "transparent",
      },
    },
  };
}

/**
 * Vega-Lite spec for a single value-over-time line with a hover rule, point
 * and tooltip (QL-356 client total value). Zero-based y-axis with compact
 * labels (1M, 2M, …); x-axis labelled "<month> <year>" using the caller's
 * localized month names, on a UTC scale so date-only strings never shift a day.
 */
export function createValueHistoryChartSpec(
  data: ValueHistoryDatum[],
  options: {
    width: number;
    height?: number;
    isDark?: boolean;
    primaryColor?: string;
    lang?: string;
    /** 12 short month names, January first. */
    monthNames: string[];
  },
): any {
  const height = options.height ?? 260;
  const isDark = options.isDark ?? false;
  const labels = pickT(T, options.lang ?? "en_GB");
  const textColor = isDark ? "#a1a1aa" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const ruleColor = isDark ? "#71717a" : "#9ca3af";
  // The chart sits on a Card: hsl(240 6% 10%) dark, white light.
  const pointFill = isDark ? "#18181b" : "#ffffff";
  const lineColor = options.primaryColor || DEFAULT_PRIMARY_COLOR;
  const yAxis = niceYAxisWithHeadroom(
    data.length > 0 ? Math.max(...data.map((d) => d.value)) : 0,
    1,
  );
  const months = JSON.stringify(options.monthNames);
  // Label every 1/3/6/12 months so a few weeks and several years both get a
  // handful of "<month> <year>" ticks.
  const spanMonths =
    data.length > 1
      ? (Date.parse(data.at(-1)!.date) - Date.parse(data[0]!.date)) / (30.44 * 86_400_000)
      : 0;
  const monthStep = spanMonths <= 6 ? 1 : spanMonths <= 18 ? 3 : spanMonths <= 48 ? 6 : 12;

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width: options.width,
    height,
    background: "transparent",
    data: { values: data },
    encoding: {
      x: {
        field: "date",
        type: "temporal",
        scale: { type: "utc" },
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          tickCount: { interval: "month", step: monthStep },
          labelAngle: 0,
          labelFlush: true,
          labelExpr: `${months}[utcmonth(datum.value)] + ' ' + utcyear(datum.value)`,
        },
      },
      y: {
        field: "value",
        type: "quantitative",
        scale: { domainMin: yAxis.domainMin, domainMax: yAxis.domainMax },
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor,
          domain: false,
          ticks: false,
          values: yAxis.tickValues,
          // Compact labels (1M, 2M, …); a bare 0 rather than Vega's "0M", and
          // billions as B rather than the SI giga "G".
          labelExpr: "datum.value === 0 ? '0' : replace(format(datum.value, '~s'), 'G', 'B')",
        },
      },
    },
    layer: [
      {
        mark: {
          type: "line",
          color: lineColor,
          strokeWidth: 2,
          clip: true,
          // A single point draws a zero-length line — show it as a dot instead.
          point: data.length === 1 ? { filled: true, fill: lineColor, size: 60 } : false,
        },
      },
      {
        transform: [{ filter: { param: "hover", empty: false } }],
        mark: {
          type: "point",
          filled: true,
          fill: pointFill,
          stroke: lineColor,
          strokeWidth: 2,
          size: 60,
        },
      },
      {
        // An invisible rule per point catches the nearest hover and carries
        // the tooltip; the hovered one turns visible as a dashed guide.
        mark: { type: "rule", color: ruleColor, strokeDash: [3, 3] },
        params: [
          {
            name: "hover",
            select: {
              type: "point",
              fields: ["date"],
              nearest: true,
              on: "pointerover",
              clear: "pointerout",
            },
          },
        ],
        encoding: {
          opacity: { condition: { param: "hover", empty: false, value: 1 }, value: 0 },
          tooltip: [
            { field: "dateLabel", type: "nominal", title: labels.date },
            { field: "valueLabel", type: "nominal", title: labels.value },
          ],
        },
      },
    ],
    config: { view: { stroke: "transparent" } },
  };
}

/**
 * Creates a Vega-Lite multi-line chart specification for comparing two series
 *
 * Used for cost charts showing account value with and without costs.
 */
export function createMultiLineChartSpec(
  data: MultiLineData[],
  options: {
    width?: number | "container";
    height?: number;
    title?: string;
    line1Label?: string; // Label for y1 (e.g., "Without Cost")
    line2Label?: string; // Label for y2 (e.g., "With Cost")
    yAxisLabel?: string;
    isDark?: boolean;
    primaryColor?: string;
    lang?: string;
  } = {},
): any {
  // Use numeric width - 'container' doesn't work reliably with layered charts
  const width = typeof options.width === "number" ? options.width : 500;
  const height = options.height || 300;
  const isDark = options.isDark || false;
  const labels = pickT(T, options.lang ?? "en_GB");

  // Theme colors
  const backgroundColor = isDark ? "transparent" : "transparent";
  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  // Zero-based y-axis with 1 gridline of headroom (DESIGN.md → Charts): a zoomed
  // non-zero baseline makes the cost drag look alarmingly large — anchor at 0 so
  // the with/without-cost gap reads proportionately.
  const dataMax = data.length > 0 ? Math.max(...data.flatMap((d) => [d.y1, d.y2])) : 0;
  const yAxis = niceYAxisWithHeadroom(dataMax, 1);

  // Line colors - gray for baseline (without cost), primary for actual (with cost)
  const line1Color = isDark ? "#9ca3af" : "#6b7280"; // Gray for without cost
  const line2Color = options.primaryColor || DEFAULT_PRIMARY_COLOR;
  const label1 = options.line1Label || "Without Cost";
  const label2 = options.line2Label || "With Cost";
  // Shared color scale so the two layers merge into a single legend (below chart).
  const colorScale = { domain: [label1, label2], range: [line1Color, line2Color] };
  const bottomLegend = { orient: "bottom", direction: "horizontal", title: null };

  // Use layered approach for better control of line styling
  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height,
    background: backgroundColor,
    data: { values: data },
    layer: [
      // First line (Without Cost) - dashed gray
      {
        mark: {
          type: "line",
          strokeWidth: 2,
          strokeDash: [4, 4],
          point: { filled: true, size: 40 },
        },
        encoding: {
          x: {
            field: "x",
            type: "ordinal",
            sort: null,
          },
          y: {
            field: "y1",
            type: "quantitative",
          },
          color: {
            datum: label1,
            type: "nominal",
            scale: colorScale,
            legend: bottomLegend,
          },
          tooltip: [
            { field: "x", type: "nominal", title: labels.period },
            {
              field: "y1",
              type: "quantitative",
              title: options.line1Label || "Without Cost",
              format: ",.2f",
            },
          ],
        },
      },
      // Second line (With Cost) - solid orange
      {
        mark: {
          type: "line",
          strokeWidth: 2,
          point: { filled: true, size: 40 },
        },
        encoding: {
          x: {
            field: "x",
            type: "ordinal",
            sort: null,
          },
          y: {
            field: "y2",
            type: "quantitative",
          },
          color: {
            datum: label2,
            type: "nominal",
            scale: colorScale,
            legend: bottomLegend,
          },
          tooltip: [
            { field: "x", type: "nominal", title: labels.period },
            {
              field: "y2",
              type: "quantitative",
              title: options.line2Label || "With Cost",
              format: ",.2f",
            },
          ],
        },
      },
    ],
    encoding: {
      x: {
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          labelAngle: 0,
        },
      },
      y: {
        scale: { domainMin: 0, domainMax: yAxis.domainMax },
        axis: {
          title: options.yAxisLabel || null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor: gridColor,
          domain: false,
          orient: "left",
          values: yAxis.tickValues,
          format: ",.0f",
        },
      },
    },
    config: {
      view: {
        stroke: "transparent",
      },
      legend: {
        labelColor: textColor,
      },
    },
  };
}

/**
 * Creates a Vega-Lite **stacked**-bar chart spec for the cost chart.
 *
 * Each bar totals the *without-cost* value (`y1`), split into two stacked
 * segments: the net **with-cost** value you keep (`y2`, bottom, primary colour)
 * and the **cost** drag on top (`y1 - y2`, gray). Same data shape and palette as
 * {@link createMultiLineChartSpec}.
 */
export function createMultiBarChartSpec(
  data: MultiLineData[],
  options: {
    width?: number | "container";
    height?: number;
    title?: string;
    line2Label?: string; // Net "with cost" value (bottom segment)
    costLabel?: string; // Cost drag (top segment) = y1 - y2
    yAxisLabel?: string;
    isDark?: boolean;
    primaryColor?: string;
    lang?: string;
  } = {},
): any {
  const width = typeof options.width === "number" ? options.width : 500;
  const height = options.height || 300;
  const isDark = options.isDark || false;
  const labels = pickT(T, options.lang ?? "en_GB");

  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  // Net (with cost) takes the prominent primary colour; the cost drag is gray.
  const costColor = isDark ? "#9ca3af" : "#6b7280";
  const netColor = options.primaryColor || DEFAULT_PRIMARY_COLOR;
  const netLabel = options.line2Label || "With Cost";
  const costLabel = options.costLabel || "Cost";

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height,
    background: "transparent",
    data: { values: data },
    transform: [
      { calculate: "datum.y2", as: "net" },
      { calculate: "max(datum.y1 - datum.y2, 0)", as: "cost" },
      { fold: ["net", "cost"], as: ["seriesKey", "value"] },
      {
        calculate: `datum.seriesKey === 'net' ? ${JSON.stringify(netLabel)} : ${JSON.stringify(costLabel)}`,
        as: "series",
      },
      // Keep the net segment at the bottom of the stack.
      { calculate: "datum.seriesKey === 'net' ? 0 : 1", as: "stackOrder" },
      // Tooltip shows the meaningful TOTAL for each series (after-cost value for
      // the net segment, before-cost value for the cost segment) — not the
      // segment height, which for the cost segment is only the delta.
      { calculate: "datum.seriesKey === 'net' ? datum.y2 : datum.y1", as: "displayValue" },
    ],
    mark: { type: "bar" },
    encoding: {
      x: {
        field: "x",
        type: "ordinal",
        sort: null,
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          labelAngle: 0,
        },
      },
      y: {
        field: "value",
        type: "quantitative",
        stack: "zero",
        axis: {
          title: options.yAxisLabel || null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor: gridColor,
          domain: false,
          orient: "left",
          tickCount: 5,
          format: ",.0f",
        },
      },
      color: {
        field: "series",
        type: "nominal",
        // Domain order = legend order. Keep it "before cost, after cost" to match
        // the line chart's legend; the colour mapping and stack order are unaffected.
        scale: { domain: [costLabel, netLabel], range: [costColor, netColor] },
        legend: { orient: "bottom", direction: "horizontal", title: null, labelColor: textColor },
      },
      order: { field: "stackOrder", type: "quantitative" },
      tooltip: [
        { field: "x", type: "nominal", title: labels.period },
        { field: "series", type: "nominal", title: " " },
        { field: "displayValue", type: "quantitative", format: ",.2f" },
      ],
    },
    config: {
      view: { stroke: "transparent" },
      legend: { labelColor: textColor },
    },
  };
}

/**
 * Creates a Vega-Lite forecast chart specification with confidence bounds
 *
 * Displays a line for expected value with a shaded confidence band.
 * Used for portfolio forecast projections; the band runs from the 5th to the 95th percentile.
 */
export function createForecastChartSpec(
  data: ForecastChartData[],
  options: {
    width?: number | "container";
    height?: number;
    title?: string;
    yAxisLabel?: string;
    isDark?: boolean;
    primaryColor?: string;
    lang?: string;
    /** Rate the bank series was generated with — shown in the legend label. */
    bankReturnPct?: number;
  } = {},
): any {
  // Use numeric width - 'container' doesn't work reliably with layered charts
  const width = typeof options.width === "number" ? options.width : 500;
  const height = options.height || 300;
  const isDark = options.isDark || false;
  const labels = pickT(T, options.lang ?? "en_GB");

  // Theme colors
  const backgroundColor = isDark ? "transparent" : "transparent";
  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  // Chart colors - use primary color for forecast
  const forecastLineColor = options.primaryColor || DEFAULT_PRIMARY_COLOR;
  const confidenceBandColor = forecastLineColor;

  // Bank-savings comparison line — same styling role as the cost chart's gray
  // baseline. Present only when the data carries a bank series; the legend then
  // names both lines (rate included so the assumption is visible on the chart).
  const hasBank = data.some((d) => typeof d.bank === "number" && Number.isFinite(d.bank));
  const bankLineColor = isDark ? "#9ca3af" : "#6b7280";
  const forecastLabel = labels.savingPlan;
  const bankLabel =
    options.bankReturnPct != null
      ? `${labels.bankSavings} – ${Number(options.bankReturnPct)}%`
      : labels.bankSavings;
  const colorScale = {
    domain: [forecastLabel, bankLabel],
    range: [forecastLineColor, bankLineColor],
  };
  const bottomLegend = {
    orient: "bottom",
    direction: "horizontal",
    title: null,
    labelColor: textColor,
  };

  // Zero-based y-axis with headroom: anchoring at 0 keeps the band from looking
  // scarier than it is, and we always leave ~2 extra gridlines above the upper
  // bound so the cone never touches the top edge.
  const upperValues = data
    .flatMap((d) => [d.upper, d.bank])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const dataMax = upperValues.length > 0 ? Math.max(...upperValues) : 0;
  const { domainMax: yDomainMax, tickValues: yTickValues } = niceYAxisWithHeadroom(dataMax, 1);

  // Values are revealed on hover (tooltip), not printed on the chart — see DESIGN.md.
  const hoverTooltip = [
    { field: "date", type: "temporal", title: labels.date, format: "%Y-%m" },
    { field: "forecast", type: "quantitative", title: labels.expected, format: ",.0f" },
    { field: "upper", type: "quantitative", title: labels.upperBound, format: ",.0f" },
    { field: "lower", type: "quantitative", title: labels.lowerBound, format: ",.0f" },
    ...(hasBank ? [{ field: "bank", type: "quantitative", title: bankLabel, format: ",.0f" }] : []),
  ];

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height,
    background: backgroundColor,
    data: { values: data },
    layer: [
      // Confidence band (shaded area between lower and upper). Carries the
      // tooltip so hovering anywhere in the band reveals the values.
      {
        mark: {
          type: "area",
          // Clip so a negative lower bound can't overflow the plot and balloon
          // the SVG height (the domain stays anchored at 0 — see line chart).
          clip: true,
          opacity: 0.2,
          color: confidenceBandColor,
        },
        encoding: {
          x: {
            field: "date",
            type: "temporal",
          },
          y: {
            field: "lower",
            type: "quantitative",
          },
          y2: {
            field: "upper",
          },
          tooltip: hoverTooltip,
        },
      },
      // Lower bound line (dotted)
      {
        mark: {
          type: "line",
          clip: true,
          strokeWidth: 1,
          strokeDash: [2, 2],
          opacity: 0.5,
        },
        encoding: {
          x: {
            field: "date",
            type: "temporal",
          },
          y: {
            field: "lower",
            type: "quantitative",
          },
          color: { value: confidenceBandColor },
        },
      },
      // Upper bound line (dotted)
      {
        mark: {
          type: "line",
          clip: true,
          strokeWidth: 1,
          strokeDash: [2, 2],
          opacity: 0.5,
        },
        encoding: {
          x: {
            field: "date",
            type: "temporal",
          },
          y: {
            field: "upper",
            type: "quantitative",
          },
          color: { value: confidenceBandColor },
        },
      },
      // Forecast line (main line). With a bank series present, both named lines
      // share the color scale so Vega merges them into one bottom legend.
      {
        mark: {
          type: "line",
          clip: true,
          strokeWidth: 2,
          point: false,
        },
        encoding: {
          x: {
            field: "date",
            type: "temporal",
          },
          y: {
            field: "forecast",
            type: "quantitative",
          },
          color: hasBank
            ? { datum: forecastLabel, type: "nominal", scale: colorScale, legend: bottomLegend }
            : { value: forecastLineColor },
          tooltip: hoverTooltip,
        },
      },
      // Bank-savings comparison line (dashed gray)
      ...(hasBank
        ? [
            {
              mark: {
                type: "line",
                clip: true,
                strokeWidth: 2,
                strokeDash: [4, 4],
              },
              encoding: {
                x: {
                  field: "date",
                  type: "temporal",
                },
                y: {
                  field: "bank",
                  type: "quantitative",
                },
                color: {
                  datum: bankLabel,
                  type: "nominal",
                  scale: colorScale,
                  legend: bottomLegend,
                },
                tooltip: hoverTooltip,
              },
            },
          ]
        : []),
    ],
    encoding: {
      x: {
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          labelAngle: 0,
          format: "%Y",
          tickCount: 5,
        },
      },
      y: {
        scale: { domainMin: 0, domainMax: yDomainMax },
        axis: {
          title: options.yAxisLabel || null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor: gridColor,
          grid: true,
          domain: false,
          orient: "left",
          values: yTickValues,
          format: ",.0f",
        },
      },
    },
    config: {
      view: {
        stroke: "transparent",
      },
    },
  };
}

/**
 * Creates a Vega-Lite 100%-normalised stacked bar specification (QL-315).
 *
 * Every bar is scaled to fill the plot, so the chart reads as a mix over time
 * rather than as amounts — a portfolio that quadruples in value draws the same
 * height, and only its composition moves. Distinct from
 * {@link createMultiBarChartSpec}, which stacks two absolute money segments.
 *
 * `seriesOrder` fixes both the stack order and the legend order, and is the
 * whole reason bands stay in the same place from bar to bar: left to Vega, the
 * stack order follows each bar's own values and the bands would shuffle
 * vertically as the mix changes. `seriesColors` must cover every key in it.
 *
 * `showLegend: false` drops Vega's own legend for a caller that names the bands
 * itself — a swatch table under the chart says the same thing and carries the
 * numbers too, so drawing both just repeats every label.
 */
export function createNormalizedStackedBarChartSpec(
  data: StackedBarDatum[],
  options: {
    seriesOrder: string[];
    seriesColors: string[];
    width?: number | "container";
    height?: number;
    isDark?: boolean;
    lang?: string;
    showLegend?: boolean;
    referenceLine?: StackedBarReferenceLine;
  },
): any {
  const width = typeof options.width === "number" ? options.width : 500;
  const height = options.height || 320;
  const isDark = options.isDark || false;
  const labels = pickT(T, options.lang ?? "en_GB");

  const textColor = isDark ? "#e5e7eb" : "#374151";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  // Rank each series by its position in seriesOrder so the stack is stable.
  const rank: Record<string, number> = {};
  options.seriesOrder.forEach((key, index) => {
    rank[key] = index;
  });

  // The bars are one layer so a reference rule can sit on top of them in the
  // same normalised y scale (0–1); without a rule the spec is that layer alone.
  const barLayer = {
    mark: { type: "bar" },
    encoding: {
      x: {
        field: "x",
        type: "ordinal",
        // `sort: null` keeps the caller's chronological order; Vega would
        // otherwise sort the labels alphabetically ("2031" before "Q2 2026").
        sort: null,
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          grid: false,
          domain: false,
          labelAngle: -45,
        },
      },
      y: {
        field: "value",
        type: "quantitative",
        stack: "normalize",
        axis: {
          title: null,
          labelColor: textColor,
          tickColor: textColor,
          gridColor: gridColor,
          domain: false,
          orient: "left",
          format: ".0%",
          values: [0, 0.25, 0.5, 0.75, 1],
        },
      },
      color: {
        field: "series",
        type: "nominal",
        scale: { domain: options.seriesOrder, range: options.seriesColors },
        // Null, not absent: Vega draws its default legend for an omitted key.
        legend:
          options.showLegend === false
            ? null
            : {
                orient: "bottom",
                direction: "horizontal",
                columns: 3,
                title: null,
                labelColor: textColor,
              },
      },
      order: { field: "stackOrder", type: "quantitative" },
      tooltip: [
        { field: "x", type: "nominal", title: labels.period },
        { field: "series", type: "nominal", title: labels.category },
        { field: "value", type: "quantitative", title: labels.percentage, format: ".1f" },
      ],
    },
  };

  const line = options.referenceLine;
  const lineColor = line?.color ?? "#dc2626";
  // The value is revealed on hover, never printed on the chart (DESIGN.md).
  const ruleLayers = line
    ? [
        {
          // One row of its own: without it the layer inherits the bar data and
          // draws one rule per band per bar on top of each other.
          data: { values: [{ label: line.label ?? `${line.value.toFixed(1)}%` }] },
          mark: { type: "rule", strokeDash: [6, 4], strokeWidth: 2, color: lineColor },
          encoding: {
            y: { datum: line.value / 100 },
            tooltip: { field: "label", type: "nominal" },
          },
        },
      ]
    : [];

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    width,
    height,
    background: "transparent",
    data: { values: data.map((d) => ({ ...d, stackOrder: rank[d.series] ?? 999 })) },
    layer: [barLayer, ...ruleLayers],
    config: {
      view: { stroke: "transparent" },
      legend: { labelColor: textColor },
    },
  };
}
